/* eslint-disable prefer-arrow-callback */
import { useCallback, useEffect, useRef, useState } from "react";

import { ViewMode } from "pages/app/scenario-analysis/utils/forecast";

type OutcomeComparisonStateArgs = {
  /** Without a comparison scenario the Difference view falls back to cumulative. */
  hasDifferenceView: boolean;
  viewRequest: { view: ViewMode; scenarioId?: string } | null;
  onViewRequestHandled?: () => void;
  onViewModeChange?: (view: ViewMode) => void;
};

/** The results view on show and the resilience scorecard's scenario tab, kept in step with the page's card marker. */
export const useOutcomeComparisonState = ({ hasDifferenceView, viewRequest, onViewRequestHandled, onViewModeChange }: OutcomeComparisonStateArgs) => {
  const [viewMode, setViewMode] = useState<ViewMode>("cumulative");

  // Survives view toggles while mounted; null resolves to the baseline.
  const [activeResilienceScenarioId, setActiveResilienceScenarioId] = useState<string | null>(null);

  // Set and report together, so the page's card marker never lags the pill a frame.
  const applyViewMode = useCallback(
    function applyViewMode(view: ViewMode) {
      setViewMode(view);
      onViewModeChange?.(view);
    },
    [onViewModeChange]
  );

  // Read through a ref so the mount report below runs once, whatever callback the page passes later.
  const onViewModeChangeRef = useRef(onViewModeChange);
  onViewModeChangeRef.current = onViewModeChange;

  // A remount shows cumulative again, so the card marker must not keep pointing at the previous view.
  useEffect(function reportCumulativeOnMount() {
    onViewModeChangeRef.current?.("cumulative");
  }, []);

  // A key insight card opens its view, on its scenario for views showing one.
  useEffect(
    function openRequestedView() {
      if (!viewRequest) return;
      applyViewMode(viewRequest.view);
      if (viewRequest.scenarioId) setActiveResilienceScenarioId(viewRequest.scenarioId);
      onViewRequestHandled?.();
    },
    [viewRequest, onViewRequestHandled, applyViewMode]
  );

  // Derived, so dropping the last comparison scenario never renders an empty Difference plot.
  const activeViewMode: ViewMode = viewMode === "difference" && !hasDifferenceView ? "cumulative" : viewMode;

  // Puts the state and the page's card marker back in step after that fallback.
  useEffect(
    function resyncAfterDifferenceFallback() {
      if (viewMode !== activeViewMode) applyViewMode(activeViewMode);
    },
    [viewMode, activeViewMode, applyViewMode]
  );

  return { activeViewMode, applyViewMode, activeResilienceScenarioId, setActiveResilienceScenarioId };
};
